import { Form } from "antd"
import { Alert, Legend, RadioButtons } from "components/common"
import { useEffect, useMemo } from "react"
import { t } from "i18next"
import FractionalPaymentItem from "./fractionalPaymentItem"
import {
  FRACTIONAL_PAYMENT_CHANNEL_PSID,
  FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
  paymentModesMapFromResponse,
  resolvePsidPaymentModeKey,
} from "./fractionalPaymentUtils"
import { PAYMENTS_FORM_LIST_NAME } from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/utils/paymentUtils"
import { channelOptions } from "./utilis"

export default function FractionalPaymentSection({
  form,
  referenceAmount,
  projectId,
  modeOfPayments,
  loadingPaymentMethods = false,
  paymentApis,
  paymentOptions,
}) {
  const { isRaFlowEnabled, ocrEnabled } = paymentOptions

  const paymentModesMap = useMemo(() => paymentModesMapFromResponse(modeOfPayments), [modeOfPayments])
  const psidPaymentModeKey = useMemo(() => resolvePsidPaymentModeKey(modeOfPayments), [modeOfPayments])

  const coercedProjectId = useMemo(() => {
    if (projectId == null || projectId === "") return null
    const n = +projectId
    return n > 0 ? n : null
  }, [projectId])

  const paymentChannel = Form.useWatch("fractionalPaymentChannel", form) ?? FRACTIONAL_PAYMENT_CHANNEL_STANDARD

  useEffect(() => {
    if (coercedProjectId == null) return
    form.setFieldValue(PAYMENTS_FORM_LIST_NAME, [{ enableDepositRA: false }])
    form.setFieldValue("fractionalPaymentChannel", FRACTIONAL_PAYMENT_CHANNEL_STANDARD)
  }, [coercedProjectId, form])

  useEffect(() => {
    if (coercedProjectId == null || !psidPaymentModeKey) return
    const { getPaymentMode, getBankName } = paymentApis
    if (paymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID) {
      form.setFieldValue([PAYMENTS_FORM_LIST_NAME, 0, "modeOfPayment"], psidPaymentModeKey)
      const meta = modeOfPayments?.find(m => m.key === psidPaymentModeKey)
      if (meta?.id) {
        getPaymentMode(meta.id, true)
        if (meta?.getBankNames) getBankName({}, true)
      }
      return
    }
    const cur = form.getFieldValue([PAYMENTS_FORM_LIST_NAME, 0, "modeOfPayment"])
    if (cur === psidPaymentModeKey) {
      form.setFieldValue([PAYMENTS_FORM_LIST_NAME, 0, "modeOfPayment"], undefined)
    }
  }, [coercedProjectId, paymentChannel, psidPaymentModeKey, modeOfPayments, form, paymentApis])

  if (coercedProjectId == null) {
    return null
  }

  if (modeOfPayments && !modeOfPayments?.length && !loadingPaymentMethods) {
    return (
      <Alert type="error" message={t("No acceptable payment methods are available in the project configurations")} />
    )
  }

  return (
    <div className="mbs-16">
      <Legend title={t("Add Payment")} style={{ marginTop: 16 }} />
      {!!psidPaymentModeKey && (
        <Form.Item name="fractionalPaymentChannel" style={{ marginTop: 12, marginBottom: 8 }}>
          <RadioButtons optionType="button" block size="middle" options={channelOptions()} />
        </Form.Item>
      )}
      <Form.List name={PAYMENTS_FORM_LIST_NAME}>
        {fields => {
          if (!fields.length) return null
          const { key, name: listFieldName, ...restField } = fields[0]
          return (
            <FractionalPaymentItem
              key={key}
              listFieldName={listFieldName}
              restField={restField}
              form={form}
              referenceAmount={referenceAmount}
              projectId={coercedProjectId}
              modeOfPayments={modeOfPayments}
              paymentModesMap={paymentModesMap}
              psidPaymentModeKey={psidPaymentModeKey}
              paymentApis={paymentApis}
              paymentOptions={{ isRaFlowEnabled, ocrEnabled }}
              paymentChannel={paymentChannel}
            />
          )
        }}
      </Form.List>
    </div>
  )
}
